import { useState } from "react";
import { useTheme } from "@mui/material";
import Modal from "components/basic/modal";
import Button from "components/basic/button";
import TextArea from "components/basic/textArea";
import Typography from "components/basic/typography";
import { useTranslation } from "react-i18next";

export interface RequestChangesModalProps {
  vendorName: string;
  estimateNumber: string;
  submitting?: boolean;
  onSubmit: (note: string) => void;
  onClose: () => void;
}

/** Asks the customer what they want changed before sending the quote back. */
const RequestChangesModal: React.FC<RequestChangesModalProps> = ({
  vendorName,
  estimateNumber,
  submitting = false,
  onSubmit,
  onClose,
}) => {
  const theme = useTheme();
  const { t } = useTranslation("quotes");
  const [note, setNote] = useState("");

  return (
    <Modal onClose={onClose}>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: theme.spacing(2),
          width: "min(480px, 80vw)",
        }}
      >
        <div>
          <Typography variant="h5">{t("changes_modal.title")}</Typography>
          <Typography
            variant="body2"
            style={{ color: theme.palette.text.secondary }}
          >
            {t("changes_modal.body", {
              vendor: vendorName,
              estimate: estimateNumber,
            })}
          </Typography>
        </div>

        <TextArea
          minRows={4}
          maxRows={8}
          value={note}
          placeHolder={t("changes_modal.placeholder")}
          ariaLabel={t("changes_modal.label")}
          onChange={(e) => setNote(e.target.value)}
        />

        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: theme.spacing(1),
          }}
        >
          <Button variant="text" onClick={onClose} disabled={submitting}>
            {t("changes_modal.cancel")}
          </Button>
          <Button
            variant="contained"
            loading={submitting}
            // A blank note would leave the vendor with nothing to act on.
            disabled={submitting || !note.trim()}
            onClick={() => onSubmit(note.trim())}
          >
            {t("changes_modal.send")}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default RequestChangesModal;
